import React, { useEffect, useState } from "react";
import { Clock, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionCountdown } from "@/hooks/useSessionCountdown";
import { cn } from "@/lib/utils";

const fmt = (n) => String(n).padStart(2, "0");

/**
 * Session timer (header chip + warning modal).
 * - Idle timeout: 30 min · warning modal at 25 min · auto sign-out at 30 min.
 * - All countdown logic lives in `useSessionCountdown`; this file is purely
 *   presentational + handles the dismiss / extend interactions.
 */
export default function SessionTimer() {
  const { sessionStartedAt, extendSession, signOut } = useAuth();
  const { remainingMs, minutes, seconds, signedOut, showWarning } =
    useSessionCountdown(sessionStartedAt);
  const [dismissedFor, setDismissedFor] = useState(null);

  // Auto sign-out when timer reaches zero.
  useEffect(() => {
    if (sessionStartedAt && remainingMs === 0) signOut();
  }, [remainingMs, sessionStartedAt, signOut]);

  if (signedOut) return null;

  const modalOpen = showWarning && dismissedFor !== sessionStartedAt;
  const isLowTime = minutes < 5;

  return (
    <>
      <TimerChip minutes={minutes} seconds={seconds} isLowTime={isLowTime} />
      {modalOpen && (
        <WarningModal
          minutes={minutes}
          seconds={seconds}
          onStay={() => {
            extendSession();
            setDismissedFor(sessionStartedAt);
          }}
          onSignOut={signOut}
        />
      )}
    </>
  );
}

function TimerChip({ minutes, seconds, isLowTime }) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-1 rounded text-meta font-mono tabular-nums",
        isLowTime ? "bg-risk-high/10 text-risk-high" : "bg-inset text-ink-secondary",
      )}
      data-testid="header-session-timer"
      title="Session time remaining"
    >
      <Clock size={11} />
      {fmt(minutes)}:{fmt(seconds)}
    </div>
  );
}

function WarningModal({ minutes, seconds, onStay, onSignOut }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-ink-primary/40 flex items-center justify-center px-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-warning-title"
      data-testid="session-warning-modal"
    >
      <div className="bg-raised border border-border-subtle rounded-lg shadow-e4 w-full max-w-[420px] p-6 anim-fade-up">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-risk-med/10 text-risk-med shrink-0">
            <ShieldAlert size={18} />
          </span>
          <div className="flex-1">
            <h3 id="session-warning-title" className="text-h3 text-ink-primary">
              Your session will end in {fmt(minutes)}:{fmt(seconds)}
            </h3>
            <p className="mt-1 text-meta text-ink-secondary">
              Stay signed in to continue. We extend in 30-min increments after
              re-verification.
            </p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onSignOut}
            className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset"
            data-testid="session-warning-signout"
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={onStay}
            className="h-9 px-4 rounded-md text-sm font-semibold bg-tralenex-600 text-white hover:bg-tralenex-700"
            data-testid="session-warning-stay"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
